"use client";

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import {
  Phone,
  Instagram,
  ChevronLeft,
  ChevronRight,
  MapPin,
  ChevronDown,
  ChevronUp,
  Download,
  Smartphone,
  X,
  Menu,
  Calendar,
  MessageCircle,
  Scissors,
} from "lucide-react";
import "./landingpage.css";
import { requestPermissionAndGetToken } from "firebase-helper";
import { AiFillInstagram } from "react-icons/ai";
import { BsInstagram } from "react-icons/bs";

// ─── Business Configuration ────────────────────────────────────────────────
const businessInfo = {
  name: "Hair Clup",
  fullName: "Hair Clup Exclusive",
  slogan: "Hair Clup Exclusive · Bekir Azman",
  phone: "05547473555",
  phoneFormatted: "0554 747 3555",
  whatsapp: "905547473555",
  instagram: "hairclup_bekir",
  address: "Mehmetçik, 2584. Sk. no:4, 20100 Pamukkale/Denizli",
  domain: "hairclupexclusive.com",
  appUrl: "https://hairclupexclusive.com/auth/sign-in",
  mapEmbed:
 "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3154.630155130896!2d29.086067!3d37.751819999999995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14c73ff73751f879%3A0xeaa2df891dfd46b4!2sHair%20Clup%20Exclusive!5e0!3m2!1str!2str!4v1778045729801!5m2!1str!2str"
 };

// ─── Services ────────────────────────────────────────────────────────────────
const services = [
  {
    id: 1,
    name: "SAKAL TASARIMI",
    sub: "Profesyonel Şekillendirme",
    image: "/images/bkrsntrk/sakalkesim_2.jpg",
  },
  {
    id: 2,
    name: "KLASİK KESIM",
    sub: "Klasik Erkek Stili",
    image: "/images/bkrsntrk/fade_bkr_2.jpg",
  },
  {
    id: 3,
    name: "FADE & TAPER",
    sub: "Modern Geçiş Teknikleri",
    image: "/images/bkrsntrk/fade.jpg",
  },
  {
    id: 4,
    name: "CİLT BAKIMI",
    sub: "Derin Temizlik & Nem",
    image: "/images/bkrsntrk/skincare.jpg",
  },
  {
    id: 5,
    name: "JUNİOR KESİM",
    sub: "Güvenli & Eğlenceli",
    image: "/images/bkrsntrk/junior.jpg",
  },
];

// ─── Barbers ─────────────────────────────────────────────────────────────────
const barbers = [
  {
    id: 1,
    name: "PERSONEL 1",
    specialty: "Fade & Sakal Tasarımı",
    exp: "12 Yıl Deneyim",
    img: "/images/bkrsntrk/barber_2.jpg",
    ig: "personel.cuts",
  },
  {
    id: 2,
    name: "PERSONEL 2",
    specialty: "Klasik Kesim & Bıyık",
    exp: "7 Yıl Deneyim",
    img: "/images/bkrsntrk/barber_1.jpg",
    ig: "personel.cuts",
  },
  {
    id: 2,
    name: "PERSONEL 3",
    specialty: "Sakal Tasarımı & Bıyık",
    exp: "5 Yıl Deneyim",
    img: "/images/bkrsntrk/about_global.jpg",
    ig: "personel.cuts",
  },
];

// ─── Gallery Items ────────────────────────────────────────────────────────────
const galleryItems = [
  { id: 1, type: "image", url: "/images/bkrsntrk/fade_bkr_2.jpg", alt: "Fade Kesim" },
  { id: 2, type: "image", url: "/images/bkrsntrk/sakalkesim_2.jpg", alt: "Sakal Tasarımı" },
  { id: 3, type: "video", url: "/images/bkrsntrk/bg_vid.mp4", alt: "Kesim Süreci" },
  { id: 4, type: "image", url: "/images/bkrsntrk/fade.jpg", alt: "Klasik Tıraş" },
  { id: 5, type: "image", url: "/images/bkrsntrk/junior.jpg", alt: "Taper Fade" },
  { id: 6, type: "image", url: "/images/bkrsntrk/skincare.jpg", alt: "Cilt Bakımı" },
];

// ─── Main Component ───────────────────────────────────────────────────────────
const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isMobileScreen, setIsMobileScreen] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  const [selectedImage, setSelectedImage] = useState(null);
  const [hoveredService, setHoveredService] = useState(null);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installModalVisible, setInstallModalVisible] = useState(false);

  const gallerySliderRef = useRef(null);
  const circleRef = useRef(null);
  const videoRef = useRef(null);

  // ── PWA Install Prompt ────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstallClick = async () => {
    setInstallModalVisible(true);
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then(() => setDeferredPrompt(null));
    }
  };

  // ── Resize ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const handleResize = () => setIsMobileScreen(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ── Video Load ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (videoRef.current && videoRef.current.readyState >= 3) {
      setIsVideoLoaded(true);
    }
    const safetyTimer = setTimeout(() => setIsVideoLoaded(true), 2000);
    return () => clearTimeout(safetyTimer);
  }, []);

  const handleVideoLoad = () => {
    setTimeout(() => setIsVideoLoaded(true), 600);
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  // ── Scroll Tracking ───────────────────────────────────────────────────────
  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "home",
        "services",
        "barbers",
        "gallery",
        "about",
        "contact",
      ];
      const scrollPosition = window.scrollY + 100;
      setShowScrollTop(window.scrollY > 300);

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(section);
            break;
          }
        }
      }

      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const scrollableHeight = documentHeight - windowHeight;
      if (scrollableHeight > 0 && circleRef.current) {
        const scrollPercent = scrollTop / scrollableHeight;
        const circumference = 2 * Math.PI * 26;
        circleRef.current.style.strokeDashoffset =
          circumference - scrollPercent * circumference;
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleBookAppointment = () => {
    navigate("/auth/sign-in");
  };

  const handleWhatsapp = () => {
    window.open(`https://wa.me/${businessInfo.whatsapp}`, "_blank");
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveSection(sectionId);
      setMenuOpen(false);
    }
  };

  const scrollToNext = () => {
    const el = document.getElementById("services");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const scrollGallery = (dir) => {
    if (!gallerySliderRef.current) return;
    const amount = window.innerWidth > 768 ? 500 : window.innerWidth * 0.88;
    gallerySliderRef.current.scrollBy({
      left: dir === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  const navLinks = [
    { id: "home", text: "Ana Sayfa" },
    { id: "services", text: "Hizmetler" },
    { id: "barbers", text: "Ekibimiz" },
    { id: "gallery", text: "Galeri" },
    { id: "about", text: "Hakkımızda" },
    { id: "contact", text: "İletişim" },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="pc-landing">
      {/* ── Loader ── */}
      <div className={`pc-loader ${isVideoLoaded ? "loaded" : ""}`}>
        <div className="pc-loader-ring" />
        <span className="pc-loader-brand">Hair Clup</span>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
                HAMBURGER BUTTON — Fixed corner
            ══════════════════════════════════════════════════════════════════ */}
      <button
        className={`pc-hamburger ${menuOpen ? "open" : ""}`}
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label="Menü"
      >
        <span className="pc-ham-line" />
        <span className="pc-ham-line" />
        <span className="pc-ham-line" />
      </button>

      {/* ══════════════════════════════════════════════════════════════════
                FULLSCREEN OVERLAY MENU
            ══════════════════════════════════════════════════════════════════ */}
      <div className={`pc-overlay-menu ${menuOpen ? "open" : ""}`}>
        <div className="pc-overlay-inner">
          <div className="pc-overlay-brand">Hair Clup Exclusive</div>
          <nav className="pc-overlay-nav">
            {navLinks.map((link, i) => (
              <button
                key={link.id}
                className={`pc-overlay-link ${
                  activeSection === link.id ? "active" : ""
                }`}
                onClick={() => scrollToSection(link.id)}
                style={{ animationDelay: `${i * 0.07}s` }}
              >
                <span className="pc-overlay-num">0{i + 1}</span>
                {link.text}
              </button>
            ))}
          </nav>
          <div className="pc-overlay-bottom">
            <button className="pc-overlay-cta" onClick={handleBookAppointment}>
              <Calendar size={16} />
              Randevu Al
            </button>
            <div className="pc-overlay-socials">
              <a
                href={`https://www.instagram.com/${businessInfo.instagram}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <BsInstagram size={20} />
              </a>
              <a
                href={`https://wa.me/${businessInfo.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageCircle size={20} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
                HERO SECTION
            ══════════════════════════════════════════════════════════════════ */}
      <section id="home" className="pc-hero">
        <div className="pc-hero-video-wrap">
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            className="pc-hero-video"
            onLoadedData={handleVideoLoad}
          >
            <source src="/images/bkrsntrk/bg_vid.mp4" type="video/mp4" />
          </video>
        </div>
        <div className="pc-hero-overlay" />

        <div className={`pc-hero-content ${!isMuted ? "transparent" : ""}`}>
          <div className="pc-hero-tag">Hair Clup Exclusive · Bekir Azman</div>
          <h1 className="pc-hero-title">
            HAIR
            <br />
            <span className="pc-hero-accent">CLUP</span>
            <br />
            EXCLUSIVE
          </h1>
          <p className="pc-hero-slogan">{businessInfo.slogan}</p>
          <button className="pc-hero-btn" onClick={handleBookAppointment}>
            <Calendar size={16} />
            RANDEVU AL
          </button>
        </div>

        {/* <button className="pc-sound-btn" onClick={toggleMute} aria-label="Ses">
          {isMuted ? "X" : "X"}
        </button> */}

        <div className="pc-scroll-hint" onClick={scrollToNext}>
          <ChevronDown size={24} />
          <span>KAYDIR</span>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
                STATS STRIP
            ══════════════════════════════════════════════════════════════════ */}
      <section className="pc-stats">
        <div className="pc-stats-inner">
          <div className="pc-stat-item">
            <span className="pc-stat-num">3500+</span>
            <span className="pc-stat-label">Mutlu Müşteri</span>
          </div>
          <div className="pc-stat-divider" />
          <div className="pc-stat-item">
            <span className="pc-stat-num">3</span>
            <span className="pc-stat-label">Uzman Berber</span>
          </div>
          <div className="pc-stat-divider" />
          <div className="pc-stat-item">
            <span className="pc-stat-num">12+</span>
            <span className="pc-stat-label">Yıl Deneyim</span>
          </div>
          <div className="pc-stat-divider" />
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
                SERVICES — Typography Hover Effect
            ══════════════════════════════════════════════════════════════════ */}
      <section id="services" className="pc-services-section">
        {/* HER HİZMET İÇİN AYRI ARKA PLAN KATMANI (Crossfade için) */}
        {services.map((service) => (
          <div
            key={`bg-layer-${service.id}`}
            className="pc-services-bg-layer"
            style={{
              backgroundImage: `url(${service.image})`,
              opacity: hoveredService?.id === service.id ? 1 : 0,
            }}
          />
        ))}

        <div
          className="pc-services-bg-dim"
          style={{ opacity: hoveredService ? 1 : 0 }}
        />

        <div className="pc-section-header">
          <span className="pc-section-tag">HİZMETLERİMİZ</span>
          <h2 className="pc-section-title">Tarzını Seç</h2>
        </div>

        <div className="pc-styles-list">
          {services.map((service) => (
            <div
              key={service.id}
              className={`pc-style-item ${
                hoveredService?.id === service.id ? "hovered" : ""
              }`}
              onMouseEnter={() => setHoveredService(service)}
              onMouseLeave={() => setHoveredService(null)}
              /* İkinci tıklamada null olma olayını sildik, sadece resmi açacak: */
              onTouchStart={() => setHoveredService(service)}
            >
              <span className="pc-style-num">0{service.id}</span>
              <span className="pc-style-name">{service.name}</span>
              <span className="pc-style-sub">{service.sub}</span>
              <span className="pc-style-arrow">→</span>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
                BARBERS SECTION
            ══════════════════════════════════════════════════════════════════ */}
      <section id="barbers" className="pc-barbers-section">
        <div className="pc-section-header">
          <span className="pc-section-tag">EKİBİMİZ</span>
          <h2 className="pc-section-title">SANATÇILAR</h2>
          <p className="pc-section-sub">Her biri kendi alanının ustası</p>
        </div>

        <div className="pc-barbers-grid">
          {barbers.map((barber) => (
            <div key={barber.id} className="pc-barber-card">
              <div className="pc-barber-img-wrap">
                <img
                  src={barber.img}
                  alt={barber.name}
                  className="pc-barber-img"
                  loading="lazy"
                />
                <div className="pc-barber-img-overlay" />
              </div>
              <div className="pc-barber-info">
                <h3 className="pc-barber-name">{barber.name}</h3>
                <span className="pc-barber-specialty">{barber.specialty}</span>
                <span className="pc-barber-exp">{barber.exp}</span>
                <a
                  href={`https://www.instagram.com/${barber.ig}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pc-barber-ig"
                >
                  <BsInstagram size={14} />@{barber.ig}
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
                GALLERY — Horizontal Scroll, Grayscale→Color on Hover
            ══════════════════════════════════════════════════════════════════ */}
      <section id="gallery" className="pc-gallery-section">
        <div className="pc-section-header">
          <span className="pc-section-tag">GALERİ</span>
          <h2 className="pc-section-title">Çalışmalarımız</h2>
        </div>

        <div className="pc-gallery-wrap">
          <button
            className="pc-gallery-btn left"
            onClick={() => scrollGallery("left")}
            aria-label="Önceki"
          >
            <ChevronLeft size={22} />
          </button>

          <div className="pc-gallery-track" ref={gallerySliderRef}>
            {galleryItems.map((item) => (
              <div
                key={item.id}
                className="pc-gallery-item"
                onClick={() => setSelectedImage(item)}
              >
                {item.type === "video" ? (
                  <video
                    src={item.url}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="pc-gallery-media"
                  />
                ) : (
                  <img
                    src={item.url}
                    alt={item.alt || "Hair Clup Exclusive - Denizli Berber"}
                    loading="lazy"
                    className="pc-gallery-media"
                  />
                )}
                <div className="pc-gallery-item-overlay">
                  <span className="pc-gallery-item-label">{item.alt}</span>
                </div>
              </div>
            ))}
          </div>

          <button
            className="pc-gallery-btn right"
            onClick={() => scrollGallery("right")}
            aria-label="Sonraki"
          >
            <ChevronRight size={22} />
          </button>
        </div>

        {/* Lightbox */}
        {selectedImage && (
          <div className="pc-lightbox" onClick={() => setSelectedImage(null)}>
            <button
              className="pc-lightbox-close"
              onClick={() => setSelectedImage(null)}
            >
              <X size={26} />
            </button>
            <div
              className="pc-lightbox-inner"
              onClick={(e) => e.stopPropagation()}
              onContextMenu={(e) => e.preventDefault()}
            >
              {selectedImage.type === "video" ? (
                <video
                  src={selectedImage.url}
                  controls
                  autoPlay
                  playsInline
                  className="pc-lightbox-media"
                />
              ) : (
                <img
                  src={selectedImage.url}
                  alt={selectedImage.alt}
                  className="pc-lightbox-media"
                  draggable="false"
                />
              )}
              {selectedImage.alt && (
                <p className="pc-lightbox-caption">{selectedImage.alt}</p>
              )}
            </div>
          </div>
        )}
      </section>

      {/* ══════════════════════════════════════════════════════════════════
                MOTTO / QUOTE SECTION
            ══════════════════════════════════════════════════════════════════ */}
      <section className="pc-motto-section">
        <div className="pc-motto-inner">
          <span className="pc-motto-quote">"</span>
          <p className="pc-motto-text">
            Bir kesim sadece saçla ilgili değildir. Özgüveninle, kimliğinle,
            kendinle ilgilidir.
          </p>
          <span className="pc-motto-author">— Bekir Azman</span>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
                ABOUT SECTION
            ══════════════════════════════════════════════════════════════════ */}
      <section id="about" className="pc-about-section">
        <div className="pc-about-grid">
          <div className="pc-about-content">
            <span className="pc-section-tag">HAKKIMIZDA</span>
            <h2 className="pc-section-title left">Hikayemiz</h2>
            <h3 className="pc-about-heading">Hair Clup Exclusive</h3>
            <p className="pc-about-text">
              Denizli Pamukkale&apos;nin kalbinde, yılların deneyimini modern
              tekniklerle buluşturan Hair Clup Exclusive, her müşterisine özel
              bir deneyim sunar. Saç kesiminden sakal tasarımına, cilt
              bakımından sıcak havlu ritüellerine uzanan geniş hizmet
              yelpazesiyle bölgenin en köklü erkek kuaförlerinden biri olmaya
              devam ediyoruz.
            </p>
            <p className="pc-about-text">
              Hijyen ve kaliteyi ön planda tutarak, her ziyarette tutarlı ve
              premium bir deneyim yaşatmayı taahhüt ediyoruz. Çünkü siz buna
              layıksınız.
            </p>
            <div className="pc-about-features">
              <div className="pc-feature-row">
                <span className="pc-feature-dot" />
                <span>Tek Kullanımlık Steril Malzeme</span>
              </div>
              <div className="pc-feature-row">
                <span className="pc-feature-dot" />
                <span>Kişiye Özel Stil Danışmanlığı</span>
              </div>
              <div className="pc-feature-row">
                <span className="pc-feature-dot" />
                <span>Online Randevu Sistemi</span>
              </div>
            </div>
          </div>
          <div className="pc-about-image-wrap">
            <img
              src="/images/bkrsntrk/about_global.jpg"
              alt="PROCUT Berber Salonu"
              className="pc-about-img"
            />
            <div className="pc-about-img-line" />
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
                CONTACT SECTION
            ══════════════════════════════════════════════════════════════════ */}
      <section id="contact" className="pc-contact-section">
        <div className="pc-section-header">
          <span className="pc-section-tag">İLETİŞİM</span>
          <h2 className="pc-section-title">Bize Ulaşın</h2>
          <p className="pc-section-sub">Randevu ve bilgi için</p>
        </div>

        <div className="pc-contact-cards">
          <a href={`tel:${businessInfo.phone}`} className="pc-contact-card">
            <div className="pc-contact-icon">
              <Phone size={26} />
            </div>
            <h3>Telefon</h3>
            <span>{businessInfo.phoneFormatted}</span>
          </a>
          <a
            href={`https://wa.me/${businessInfo.whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            className="pc-contact-card"
          >
            <div className="pc-contact-icon pc-wa-icon">
              <MessageCircle size={26} />
            </div>
            <h3>WhatsApp</h3>
            <span>Mesaj Gönder</span>
          </a>
          <a
            href={`https://www.instagram.com/${businessInfo.instagram}`}
            target="_blank"
            rel="noopener noreferrer"
            className="pc-contact-card"
          >
            <div className="pc-contact-icon pc-ig-icon">
              <AiFillInstagram size={26} />
            </div>
            <h3>Instagram</h3>
            <span>@{businessInfo.instagram}</span>
          </a>
        </div>

        <div className="pc-contact-info-row">
          <div className="pc-info-block">
            <MapPin size={22} className="pc-info-icon" />
            <div>
              <h4>Adres</h4>
              <p>{businessInfo.address}</p>
            </div>
          </div>
          <div className="pc-info-block">
            <Calendar size={22} className="pc-info-icon" />
            <div>
              <h4>Çalışma Saatleri</h4>
              <p>Pazartesi – Cumartesi: 09:00 – 20:00</p>
              <p>Pazar: KAPALI</p>
            </div>
          </div>
        </div>

        <div className="pc-map-wrap">
          <iframe
            src={businessInfo.mapEmbed}
            width="100%"
            height="100%"
            style={{
              border: 0,
              filter: "grayscale(100%) invert(90%) contrast(1.1)",
            }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Bekir Azman Hair Clupa Denizli Berber Konum"
          />
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
                CALL TO ACTION — Full Width
            ══════════════════════════════════════════════════════════════════ */}
      <section className="pc-cta-section">
        <div className="pc-cta-inner">
          <span className="pc-cta-tag">HAZIR MISIN?</span>
          <h2 className="pc-cta-title">
            YENİ BİR
            <br />
            TARZA
            <br />
            ADIM AT
          </h2>
          <p className="pc-cta-sub">Randevunu al, farkını hisset.</p>
          <button className="pc-cta-btn" onClick={handleBookAppointment}>
            <Calendar size={18} />
            RANDEVU AL
          </button>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
                FOOTER
            ══════════════════════════════════════════════════════════════════ */}
      <footer className="pc-footer">
        <div className="pc-footer-inner">
          <div className="pc-footer-brand">
            <span className="pc-footer-logo-text">Hair Clup Exclusive</span>
            <p className="pc-footer-slogan">{businessInfo.slogan}</p>
          </div>
          <div className="pc-footer-copy">
            <p>© 2026 Hair Clup Exclusive. Tüm hakları saklıdır.</p>
            <p className="pc-footer-powered">
              Powered by{" "}
              <a
                href="https://www.vireliosoft.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Vireliosoft
              </a>{" "}
              /{" "}
              <a
                href="https://www.randevupp.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                RandevUpp
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* ══════════════════════════════════════════════════════════════════
                SCROLL TO TOP
            ══════════════════════════════════════════════════════════════════ */}
      {showScrollTop && (
        <button
          className="pc-scroll-top"
          onClick={scrollToTop}
          aria-label="Yukarı Çık"
        >
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 60 60"
            className="pc-progress-ring"
          >
            <circle
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="4"
              fill="transparent"
              r="26"
              cx="30"
              cy="30"
            />
            <circle
              ref={circleRef}
              stroke="#8a8a8a"
              strokeWidth="4"
              fill="transparent"
              r="26"
              cx="30"
              cy="30"
              style={{
                strokeDasharray: `${2 * Math.PI * 26}`,
                strokeDashoffset: `${2 * Math.PI * 26}`,
              }}
            />
          </svg>
          <ChevronUp size={20} className="pc-scroll-icon" />
        </button>
      )}

      {/* ══════════════════════════════════════════════════════════════════
                MOBILE BOTTOM BAR
            ══════════════════════════════════════════════════════════════════ */}
      <div className="pc-bottom-bar">
        <div className="pc-bottom-pill">
          <button
            className="pc-bottom-btn install-btn"
            onClick={handleInstallClick}
          >
            <Download size={18} />
            <span>İndir</span>
          </button>
          <div className="pc-bottom-divider" />
          <button
            className="pc-bottom-btn appt-btn"
            onClick={handleBookAppointment}
          >
            <Calendar size={18} />
            <span>Randevu</span>
          </button>
          <div className="pc-bottom-divider" />
          <button className="pc-bottom-btn wa-btn" onClick={handleWhatsapp}>
            <MessageCircle size={18} />
            <span>WhatsApp</span>
          </button>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
                INSTALL MODAL
            ══════════════════════════════════════════════════════════════════ */}
      {installModalVisible && (
        <div
          className="pc-modal-overlay"
          onClick={() => setInstallModalVisible(false)}
        >
          <div
            className="pc-install-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pc-modal-handle" />
            <div className="pc-modal-header">
              <h5>Uygulamayı Yükle</h5>
              <button
                className="pc-modal-close"
                onClick={() => setInstallModalVisible(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="pc-modal-body">
              <div className="pc-modal-step">
                <div className="pc-modal-step-icon">
                  <Smartphone size={22} />
                </div>
                <div>
                  <strong>iPhone / Safari</strong>
                  <p>Paylaş butonuna dokun → &quot;Ana Ekrana Ekle&quot; seç</p>
                </div>
              </div>
              <div className="pc-modal-step">
                <div className="pc-modal-step-icon">
                  <Download size={22} />
                </div>
                <div>
                  <strong>Android / Chrome</strong>
                  <p>Menü (⋮) → &quot;Ana Ekrana Ekle&quot; seç</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
